import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import {
  House, Ticket, FileText, Books, ClipboardText,
  HardDrives, Users, Robot, ClockCountdown,
  Bell, SignOut, ListDashes, UsersThree, ShieldCheck, Package, UserCircle, SidebarSimple,
  WhatsappLogo, List, X, CaretDown, Question,
  Wrench, CurrencyInr, Broadcast,
  Megaphone, Gear,
} from '@phosphor-icons/react';

import OverviewPage from './OverviewPage';
import ProfilePage from './ProfilePage';
import TicketsLayout from '../modules/tickets/TicketsLayout';
import MyRequestsPage from '../modules/tickets/pages/MyRequestsPage';
import FormBuilderPage from '../modules/tickets/pages/FormBuilderPage';
import DataSourcesPage from '../modules/tickets/pages/DataSourcesPage';
import AuditLogPage from '../modules/tickets/pages/AuditLogPage';
import ProductsPage from '../modules/products/ProductsPage';
import EmailClientMappingPage from '../modules/products/EmailClientMappingPage';
import WorkforceLayout from '../modules/workforce/WorkforceLayout';
import WhatsAppTasksLayout from '../modules/whatsapp-tasks/WhatsAppTasksLayout';
import SupportingToolsLayout from '../modules/supporting-tools/SupportingToolsLayout';
import SupportingToolsLiveLayout from '../modules/supporting-tools/SupportingToolsLiveLayout';
import AiAssistantWidget from '../modules/ai-assistant/AiAssistantWidget';
import ManagerTeamPage from '../modules/tickets/pages/ManagerTeamPage';
import KnowledgePage from './KnowledgePage';
import HelpPage from '../modules/help/HelpPage';
import HelpTourRunner from '../modules/help/tours/HelpTourRunner';
import FieldHints from '../modules/help/FieldHints';
import ServiceCatalogPage from './ServiceCatalogPage';
import AssetsLayout from '../modules/assets/AssetsLayout';
import PayrollLayout from '../modules/payroll/PayrollLayout';
import UsersPage from './UsersPage';
import AutomationPage from './AutomationPage';
import SLAPage from './SLAPage';
import TestExecutionDashboard from './TestExecutionDashboard';
import AdminLookupsPage from './AdminLookupsPage';
import AdminSettingsPage from './AdminSettingsPage';
import ModuleAccessPage from './ModuleAccessPage';
import AccessControlPage from './AccessControlPage';
import AnnouncementsAdminPage from './AnnouncementsAdminPage';
import AnnouncementPopup from './AnnouncementPopup';
import { AppBreadcrumbs } from './module-ui';
import { SIDEBAR_GROUPS } from '../navigation/routeRegistry';

import { apiPath } from '../config/apiBase';
import { getDisplayTimeZone, setDisplayTimeZone } from '../modules/tickets/utils/datetime';

const API = apiPath('/api');
const SIDEBAR_OPEN_KEY = 'wd_sidebar_groups';
const SIDEBAR_COLLAPSED_KEY = 'wd_sidebar_collapsed';
const SIDEBAR_WIDTH_KEY = 'wd_sidebar_width';
const SIDEBAR_DEFAULT_WIDTH = 256;
const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_MAX_WIDTH = 420;
const SIDEBAR_COLLAPSED_WIDTH = 72;

function loadSidebarWidth() {
  try {
    const n = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY), 10);
    if (Number.isFinite(n) && n >= SIDEBAR_MIN_WIDTH && n <= SIDEBAR_MAX_WIDTH) return n;
  } catch {
    /* ignore */
  }
  return SIDEBAR_DEFAULT_WIDTH;
}

const MENU_ITEMS = [
  { moduleKey: 'overview', path: '/dashboard', icon: House, label: 'Overview' },
  { moduleKey: 'tickets', path: '/dashboard/tickets', icon: Ticket, label: 'Tickets' },
  { moduleKey: 'my_teams', path: '/dashboard/my-teams', icon: UsersThree, label: 'My Teams' },
  { moduleKey: 'my_requests', path: '/dashboard/my-requests', icon: Ticket, label: 'My Requests' },
  { moduleKey: 'knowledge', path: '/dashboard/knowledge', icon: Books, label: 'Knowledge Base' },
  { moduleKey: 'service_catalog', path: '/dashboard/service-catalog', icon: ClipboardText, label: 'Service Catalog' },
  { moduleKey: 'assets', path: '/dashboard/assets', icon: HardDrives, label: 'Assets' },
  { moduleKey: 'payroll', path: '/dashboard/payroll', icon: CurrencyInr, label: 'Accounts' },
  { moduleKey: 'users', path: '/dashboard/users', icon: Users, label: 'Users' },
  { moduleKey: 'product_management', path: '/dashboard/products', icon: Package, label: 'Product Management' },
  { moduleKey: 'workforce', path: '/dashboard/workforce', icon: UsersThree, label: 'Work Intelligence' },
  { moduleKey: 'whatsapp_tasks', path: '/dashboard/whatsapp-tasks', icon: WhatsappLogo, label: 'WhatsApp Ticket Tracking' },
  { moduleKey: 'supporting_tools', path: '/dashboard/supporting-tools', icon: Wrench, label: 'Supporting Tools' },
  { moduleKey: 'supporting_tools_live', path: '/dashboard/supporting-tools-live', icon: Broadcast, label: 'Supporting Tools Live' },
  { moduleKey: 'automation', path: '/dashboard/automation', icon: Robot, label: 'Automation' },
  { moduleKey: 'sla', path: '/dashboard/sla', icon: ClockCountdown, label: 'SLA Management' },
  { moduleKey: 'form_builder', path: '/dashboard/form-builder', icon: FileText, label: 'Form Builder' },
  { moduleKey: 'data_sources', path: '/dashboard/data-sources', icon: FileText, label: 'Data Sources' },
  { moduleKey: 'audit_log', path: '/dashboard/audit', icon: ClipboardText, label: 'Audit Log' },
  { moduleKey: 'admin_lookups', path: '/dashboard/admin/lookups', icon: ListDashes, label: 'Manage Dropdowns' },
  { moduleKey: 'admin_settings', path: '/dashboard/admin/settings', icon: Gear, label: 'Settings' },
  { moduleKey: 'module_access', path: '/dashboard/admin/module-access', icon: ShieldCheck, label: 'Module Access' },
  { moduleKey: 'access_control', path: '/dashboard/admin/access-control', icon: ShieldCheck, label: 'Access Control' },
  { moduleKey: 'announcements', path: '/dashboard/admin/announcements', icon: Megaphone, label: 'Announcements' },
  { moduleKey: 'test_executions', path: '/dashboard/test-executions', icon: FileText, label: 'Test Executions' },
  { moduleKey: 'profile', path: '/dashboard/profile', icon: UserCircle, label: 'My Profile' },
  { moduleKey: 'help', path: '/dashboard/help', icon: Question, label: 'Help' },
];

const HIDDEN_MODULES = ['service_catalog', 'automation', 'test_executions'];

function loadGroupOpenState() {
  try {
    const raw = localStorage.getItem(SIDEBAR_OPEN_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return Object.fromEntries(SIDEBAR_GROUPS.map((g) => [g.id, true]));
}

function loadSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function Dashboard() {
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(loadSidebarCollapsed);
  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth);
  const [sidebarResizing, setSidebarResizing] = useState(false);
  const [groupOpen, setGroupOpen] = useState(loadGroupOpenState);
  const [displayClock, setDisplayClock] = useState(getDisplayTimeZone);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/settings/display`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (res.data?.timezone) setDisplayTimeZone(res.data.timezone);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const onTz = () => setDisplayClock(getDisplayTimeZone());
    window.addEventListener('wd-timezone', onTz);
    return () => window.removeEventListener('wd-timezone', onTz);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_OPEN_KEY, JSON.stringify(groupOpen));
  }, [groupOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
    } catch {
      /* ignore */
    }
  }, [sidebarWidth]);

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setSidebarOpen((open) => !open);
      return;
    }
    setSidebarCollapsed((c) => !c);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const role = user?.role === 'end_user' ? 'client' : user?.role;
  const allowedModules = user?.allowed_modules || [];

  const visibleMenuItems = useMemo(() => {
    const seen = new Set();
    return MENU_ITEMS.filter((item) => {
      if (seen.has(item.moduleKey)) return false;
      seen.add(item.moduleKey);
      if (item.moduleKey === 'profile') return true;
      if (item.moduleKey === 'help') return true;
      if (item.moduleKey === 'admin_settings') return role === 'super_admin' || role === 'admin';
      if (HIDDEN_MODULES.includes(item.moduleKey)) return false;
      return allowedModules.includes(item.moduleKey);
    });
  }, [allowedModules, role]);

  const groupedNav = useMemo(() => {
    const byKey = Object.fromEntries(visibleMenuItems.map((i) => [i.moduleKey, i]));
    return SIDEBAR_GROUPS.map((group) => ({
      ...group,
      items: group.moduleKeys.map((k) => byKey[k]).filter(Boolean),
    })).filter((g) => g.items.length > 0);
  }, [visibleMenuItems]);

  const toggleGroup = (id) => {
    setGroupOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const onSidebarResizeStart = (e) => {
    if (sidebarCollapsed) return;
    if (typeof window !== 'undefined' && window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = sidebarWidth;
    setSidebarResizing(true);

    const onMove = (ev) => {
      const next = Math.min(
        SIDEBAR_MAX_WIDTH,
        Math.max(SIDEBAR_MIN_WIDTH, startW + (ev.clientX - startX)),
      );
      setSidebarWidth(next);
    };
    const onUp = () => {
      setSidebarResizing(false);
      document.body.classList.remove('sidebar-resizing');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    document.body.classList.add('sidebar-resizing');
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const activeSidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth;
  const layoutStyle = {
    '--sidebar-width': `${activeSidebarWidth}px`,
  };

  const unread = notifications.filter((n) => !n.read);
  const unreadCount = unread.length;

  const markRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/${notificationId}/read`, null, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === notificationId ? { ...n, read: true } : n)),
      );
    } catch (e) {
      console.error(e);
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/read-all`, null, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) {
      // Fallback: mark each unread individually
      await Promise.all(unread.map((n) => markRead(n.notification_id)));
    }
  };

  const renderNavLink = (item) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/dashboard'}
      onClick={closeSidebar}
      className={({ isActive }) => `sidebar-nav-link${isActive ? ' active-link' : ''}`}
      title={item.label}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        padding: '4px 10px',
        color: isActive ? 'white' : '#9CA3AF',
        textDecoration: 'none',
        fontSize: '12px',
        fontWeight: '500',
        background: isActive ? '#002FA7' : 'transparent',
        borderLeft: isActive ? '3px solid white' : '3px solid transparent',
        transition: 'all 0.2s',
      })}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <item.icon size={18} style={{ marginRight: '10px', flexShrink: 0 }} weight="regular" />
      <span className="sidebar-nav-label">{item.label}</span>
    </NavLink>
  );

  if (role === 'pending') {
    return (
      <div className={`dashboard-layout${sidebarOpen ? ' sidebar-open' : ''}`}>
        {sidebarOpen && (
          <button
            type="button"
            className="sidebar-backdrop"
            aria-label="Close menu"
            onClick={closeSidebar}
            data-testid="sidebar-backdrop"
          />
        )}
        <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
          <div className="sidebar-header">
            <h2 style={{ color: 'white', fontSize: '16px', fontWeight: '700', margin: 0 }}>EasyITSM</h2>
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={closeSidebar}
              aria-label="Close menu"
              data-testid="sidebar-close-button"
            >
              <X size={22} weight="bold" color="#E5E7EB" />
            </button>
          </div>
          <nav style={{ padding: '8px 0' }}>
            <NavLink
              to="/dashboard/profile"
              onClick={closeSidebar}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                padding: '4px 10px',
                color: isActive ? 'white' : '#9CA3AF',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: '500',
                background: isActive ? '#002FA7' : 'transparent',
              })}
            >
              <UserCircle size={18} style={{ marginRight: '10px' }} />
              My Profile
            </NavLink>
          </nav>
        </aside>
        <div className="main-content">
          <header className="topbar">
            <div className="topbar-inner">
              <div className="topbar-left">
                <button
                  type="button"
                  className="sidebar-toggle-btn"
                  onClick={toggleSidebar}
                  aria-label={sidebarOpen ? 'Hide menu' : 'Open menu'}
                  aria-expanded={sidebarOpen}
                  data-testid="sidebar-toggle-button"
                >
                  {sidebarOpen ? <X size={24} weight="bold" color="#111827" /> : <List size={24} weight="bold" color="#111827" />}
                </button>
              </div>
              <AppBreadcrumbs />
            </div>
          </header>
          <main className="content-area">
            <ProfilePage />
          </main>
        </div>
      </div>
    );
  }

  const hasAiAssistant = allowedModules.includes('ai_assistant');

  return (
    <div
      className={`dashboard-layout${sidebarOpen ? ' sidebar-open' : ''}${sidebarCollapsed ? ' sidebar-collapsed' : ''}${sidebarResizing ? ' is-resizing' : ''}`}
      style={layoutStyle}
    >
      {sidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close menu"
          onClick={closeSidebar}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-brand-title">EasyITSM</h2>
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={closeSidebar}
            aria-label="Close menu"
            data-testid="sidebar-close-button"
          >
            <X size={22} weight="bold" color="#E5E7EB" />
          </button>
        </div>

        <nav style={{ padding: '8px 0' }} aria-label="Main">
          {groupedNav.map((group) => {
            const open = groupOpen[group.id] !== false;
            const letter = (group.label || group.id || '?').charAt(0).toUpperCase();
            return (
              <div key={group.id} className="sidebar-group">
                <button
                  type="button"
                  className="sidebar-group-toggle"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={open}
                  title={sidebarCollapsed ? group.label : undefined}
                >
                  <span className="sidebar-group-label">{group.label}</span>
                  <span className="sidebar-group-letter" aria-hidden={!sidebarCollapsed}>
                    {letter}
                  </span>
                  <CaretDown
                    className="sidebar-group-caret"
                    size={12}
                    weight="bold"
                    style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }}
                  />
                </button>
                <div className={`sidebar-group-items${open ? '' : ' is-collapsed'}`}>
                  {group.items.map(renderNavLink)}
                </div>
              </div>
            );
          })}
        </nav>

        {!sidebarCollapsed && (
          <div
            className="sidebar-resize-handle"
            onPointerDown={onSidebarResizeStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            title="Drag to resize sidebar"
            data-testid="sidebar-resize-handle"
          />
        )}
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div className="topbar-inner">
            <div className="topbar-left">
              <button
                type="button"
                className="sidebar-toggle-btn"
                onClick={toggleSidebar}
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!sidebarCollapsed || sidebarOpen}
                data-testid="sidebar-toggle-button"
                title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              >
                {sidebarOpen
                  ? <X size={24} weight="bold" color="#111827" />
                  : <SidebarSimple size={24} weight={sidebarCollapsed ? 'bold' : 'regular'} color="#111827" />}
              </button>
            </div>

            <AppBreadcrumbs />

            <div className="topbar-actions">
              <button
                type="button"
                onClick={() => setShowNotifications(!showNotifications)}
                style={{
                  position: 'relative',
                  background: 'transparent',
                  border: 'none',
                  padding: '8px',
                  cursor: 'pointer',
                }}
                data-testid="notifications-button"
                aria-expanded={showNotifications}
              >
                <Bell size={20} weight="regular" color="#111827" />
                {unreadCount > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: '#EF4444',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: '600',
                    }}
                    data-testid="notification-count-badge"
                  >
                    {unreadCount}
                  </span>
                )}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '12px', borderLeft: '1px solid #E5E7EB' }}>
                <NavLink
                  to="/dashboard/profile"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: 'inherit' }}
                  title="My Profile"
                >
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: '#002FA7',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '12px',
                      }}
                    >
                      {user?.name?.charAt(0)}
                    </div>
                  )}
                  <div className="topbar-user-text">
                    <p style={{ fontSize: '12px', fontWeight: '600', color: '#111827', margin: 0 }} data-testid="user-name">{user?.name}</p>
                    <p style={{ fontSize: '11px', color: '#6B7280', margin: 0 }} data-testid="user-role">{user?.role?.replace('_', ' ')}</p>
                  </div>
                </NavLink>
                <button
                  type="button"
                  onClick={handleLogout}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '8px',
                    cursor: 'pointer',
                  }}
                  data-testid="logout-button"
                >
                  <SignOut size={20} weight="regular" color="#EF4444" />
                </button>
              </div>
            </div>
          </div>

          {showNotifications && (
            <div className="wd-notif-panel" data-testid="notifications-dropdown">
              <div className="wd-notif-header">
                <h3>Notifications</h3>
                <div className="wd-notif-actions">
                  {unreadCount > 0 ? (
                    <button type="button" onClick={markAllRead}>
                      Mark all read
                    </button>
                  ) : null}
                  <button type="button" onClick={() => setShowNotifications(false)}>
                    Close
                  </button>
                </div>
              </div>
              <div className="wd-notif-list">
                {notifications.length === 0 ? (
                  <div className="wd-notif-empty">No notifications yet.</div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.notification_id}
                      className="wd-notif-item"
                      style={{ opacity: notif.read ? 0.65 : 1, fontWeight: notif.read ? 400 : 600 }}
                      data-testid="notification-item"
                      onClick={() => !notif.read && markRead(notif.notification_id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !notif.read) markRead(notif.notification_id);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      {notif.message}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </header>

        <main className="content-area" key={displayClock}>
          <Routes>
            <Route index element={<OverviewPage />} />
            <Route path="tickets/*" element={<TicketsLayout />} />
            <Route path="my-teams" element={<ManagerTeamPage />} />
            <Route path="workforce/*" element={<WorkforceLayout />} />
            <Route path="whatsapp-tasks/*" element={<WhatsAppTasksLayout />} />
            <Route path="supporting-tools/*" element={<SupportingToolsLayout />} />
            <Route path="supporting-tools-live/*" element={<SupportingToolsLiveLayout />} />
            <Route path="ai-assistant" element={<Navigate to="/dashboard" replace />} />
            <Route path="knowledge" element={<KnowledgePage />} />
            <Route path="service-catalog" element={<ServiceCatalogPage />} />
            <Route path="assets/*" element={<AssetsLayout />} />
            <Route path="payroll/*" element={<PayrollLayout />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="my-requests" element={<MyRequestsPage />} />
            <Route path="form-builder" element={<FormBuilderPage />} />
            <Route path="data-sources" element={<DataSourcesPage />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="products/email/:emailId/mapping" element={<EmailClientMappingPage />} />
            <Route path="automation" element={<AutomationPage />} />
            <Route path="sla" element={<SLAPage />} />
            <Route path="admin/lookups" element={<AdminLookupsPage />} />
            <Route path="admin/settings" element={<AdminSettingsPage />} />
            <Route path="admin/module-access" element={<ModuleAccessPage />} />
            <Route path="admin/access-control" element={<AccessControlPage />} />
            <Route path="admin/announcements" element={<AnnouncementsAdminPage />} />
            <Route path="test-executions" element={<TestExecutionDashboard />} />
          </Routes>
        </main>
      </div>

      {hasAiAssistant && <AiAssistantWidget />}
      <HelpTourRunner />
      <FieldHints />
      <AnnouncementPopup />
    </div>
  );
}

export default Dashboard;
